/** Enums de domínio usados pelo banco, pela API e pela interface. */

export const CLIENT_STATUSES = ['lead', 'active', 'paused', 'archived'] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];
export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  lead: 'Lead',
  active: 'Ativo',
  paused: 'Pausado',
  archived: 'Arquivado',
};

export const LABEL_COLORS = ['cyan', 'amber', 'lavender', 'mint', 'coral', 'sky', 'rose', 'sand'] as const;
export type LabelColorKey = (typeof LABEL_COLORS)[number];

export const BRAND_ASSET_KINDS = [
  'logo',
  'logo_variation',
  'color',
  'font',
  'element',
  'texture',
  'template',
  'brandbook',
  'voice',
  'link',
] as const;
export type BrandAssetKind = (typeof BRAND_ASSET_KINDS)[number];
/** Tipos de asset que são arquivos (precisam de upload). */
export const FILE_BRAND_KINDS: readonly BrandAssetKind[] = [
  'logo',
  'logo_variation',
  'element',
  'texture',
  'template',
  'brandbook',
];

export const FILE_KINDS = [
  'image',
  'video',
  'gif',
  'pdf',
  'audio',
  'archive',
  'design',
  'document',
  'font',
  'other',
] as const;
export type FileKind = (typeof FILE_KINDS)[number];

/** Onde o arquivo é usado: define a regra de permissão do upload. Cresce a cada fase. */
export const FILE_PURPOSES = [
  'avatar',
  'client_logo',
  'client_file',
  'brand_asset',
  'project_file',
  'project_cover',
  'task_attachment',
  'request_attachment',
  /** Biblioteca do estúdio (sem cliente nem projeto). */
  'studio_file',
  'board_file',
  'storyboard_file',
] as const;
export type FilePurpose = (typeof FILE_PURPOSES)[number];

/** Limite por arquivo. Acima de MULTIPART_THRESHOLD o envio é feito em partes. */
export const MAX_UPLOAD_BYTES = 20 * 1024 ** 3;
export const MAX_IMAGE_UPLOAD_BYTES = 20 * 1024 ** 2;

const EXT_KIND: Record<string, FileKind> = {
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  webp: 'image',
  svg: 'image',
  avif: 'image',
  heic: 'image',
  gif: 'gif',
  mp4: 'video',
  mov: 'video',
  webm: 'video',
  m4v: 'video',
  mkv: 'video',
  pdf: 'pdf',
  mp3: 'audio',
  wav: 'audio',
  aac: 'audio',
  m4a: 'audio',
  ogg: 'audio',
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  psd: 'design',
  ai: 'design',
  aep: 'design',
  prproj: 'design',
  fig: 'design',
  sketch: 'design',
  indd: 'design',
  c4d: 'design',
  blend: 'design',
  eps: 'design',
  ttf: 'font',
  otf: 'font',
  woff: 'font',
  woff2: 'font',
  doc: 'document',
  docx: 'document',
  xls: 'document',
  xlsx: 'document',
  ppt: 'document',
  pptx: 'document',
  txt: 'document',
  key: 'document',
  csv: 'document',
};

export function fileKindFrom(name: string, mime: string): FileKind {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  if (EXT_KIND[ext]) return EXT_KIND[ext];
  if (mime === 'image/gif') return 'gif';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  return 'other';
}

/**
 * Status do projeto. `adjusting` e `awaiting_approval` formam, com os demais, as etapas do quadro do
 * cliente. `on_hold` ficou só por compatibilidade: pausar agora é um selo (`pausedAt`), não uma etapa.
 */
export const PROJECT_STATUSES = [
  'planning',
  'active',
  'adjusting',
  'awaiting_approval',
  'on_hold',
  'completed',
  'cancelled',
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  planning: 'Planejamento',
  active: 'Em produção',
  adjusting: 'Em ajuste',
  awaiting_approval: 'Aguardando aprovação',
  on_hold: 'Pausado',
  completed: 'Concluído',
  cancelled: 'Cancelado',
};

/** Etapas do quadro de produção do cliente (projetos e tarefas avulsas juntos). */
export const CLIENT_STAGES = ['planning', 'production', 'adjusting', 'awaiting_approval', 'done'] as const;
export type ClientStage = (typeof CLIENT_STAGES)[number];
export const CLIENT_STAGE_LABELS: Record<ClientStage, string> = {
  planning: 'Planejamento',
  production: 'Em produção',
  adjusting: 'Em ajuste',
  awaiting_approval: 'Aguardando aprovação',
  done: 'Concluído',
};
const STAGE_PROJECT: Record<ClientStage, ProjectStatus> = {
  planning: 'planning',
  production: 'active',
  adjusting: 'adjusting',
  awaiting_approval: 'awaiting_approval',
  done: 'completed',
};
export const projectStatusOfStage = (s: ClientStage): ProjectStatus => STAGE_PROJECT[s];
export function stageOfProjectStatus(s: ProjectStatus): ClientStage | null {
  if (s === 'on_hold') return 'production';
  if (s === 'cancelled') return null;
  return (
    (Object.entries(STAGE_PROJECT).find(([, v]) => v === s)?.[0] as ClientStage | undefined) ?? 'planning'
  );
}

export const PRIORITIES = ['urgent', 'high', 'medium', 'low'] as const;
export type Priority = (typeof PRIORITIES)[number];
export const PRIORITY_LABELS: Record<Priority, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

export const PROJECT_MEMBER_ROLES = ['lead', 'member', 'viewer'] as const;
export type ProjectMemberRole = (typeof PROJECT_MEMBER_ROLES)[number];
export const PROJECT_MEMBER_ROLE_LABELS: Record<ProjectMemberRole, string> = {
  lead: 'Líder',
  member: 'Membro',
  viewer: 'Leitor',
};

/**
 * Categoria de cada etapa do workflow: dá significado às colunas, independente do nome
 * (relatórios, progresso, automações e o que o cliente vê dependem disso).
 */
export const STATUS_CATEGORIES = [
  'backlog',
  'active',
  'review',
  'rework',
  'client',
  'approved',
  'done',
  'cancelled',
] as const;
export type StatusCategory = (typeof STATUS_CATEGORIES)[number];
export const STATUS_CATEGORY_LABELS: Record<StatusCategory, string> = {
  backlog: 'Entrada',
  active: 'Em produção',
  review: 'Revisão interna',
  rework: 'Em ajuste',
  client: 'Com o cliente',
  approved: 'Aprovado',
  done: 'Entregue',
  cancelled: 'Cancelado',
};
/** Etapa do quadro do cliente de cada categoria de workflow (cancelada não aparece). */
const CATEGORY_STAGE: Record<StatusCategory, ClientStage | null> = {
  backlog: 'planning',
  active: 'production',
  review: 'production',
  rework: 'adjusting',
  client: 'awaiting_approval',
  approved: 'done',
  done: 'done',
  cancelled: null,
};
export const stageOfCategory = (c: StatusCategory): ClientStage | null => CATEGORY_STAGE[c];
/** Categorias que levam a uma etapa, na ordem de preferência ao mover. */
export function categoriesOfStage(s: ClientStage): StatusCategory[] {
  if (s === 'done') return ['done', 'approved'];
  if (s === 'production') return ['active', 'review'];
  return (Object.entries(CATEGORY_STAGE) as [StatusCategory, ClientStage | null][])
    .filter(([, v]) => v === s)
    .map(([k]) => k);
}
/** Etapas finais: a tarefa conta como concluída. */
export const DONE_CATEGORIES: readonly StatusCategory[] = ['done', 'cancelled'];

/** Workflow padrão (SPEC §10). */
export const DEFAULT_WORKFLOW: {
  name: string;
  category: StatusCategory;
  color: LabelColorKey;
  clientVisible: boolean;
}[] = [
  { name: 'Entrada', category: 'backlog', color: 'sand', clientVisible: true },
  { name: 'Briefing', category: 'active', color: 'lavender', clientVisible: false },
  { name: 'Pesquisa', category: 'active', color: 'lavender', clientVisible: false },
  { name: 'Criação', category: 'active', color: 'cyan', clientVisible: true },
  { name: 'Revisão interna', category: 'review', color: 'amber', clientVisible: false },
  { name: 'Cliente', category: 'client', color: 'coral', clientVisible: true },
  { name: 'Ajustes', category: 'rework', color: 'sky', clientVisible: true },
  { name: 'Aprovado', category: 'approved', color: 'mint', clientVisible: true },
  { name: 'Entregue', category: 'done', color: 'mint', clientVisible: true },
];

export const SERVICE_UNITS = ['piece', 'second', 'minute', 'hour', 'project'] as const;
export type ServiceUnit = (typeof SERVICE_UNITS)[number];
export const SERVICE_UNIT_LABELS: Record<ServiceUnit, string> = {
  piece: 'peça',
  second: 'segundo',
  minute: 'minuto',
  hour: 'hora',
  project: 'projeto',
};

/** Biblioteca inicial de materiais (SPEC §12). Preços em centavos, editáveis depois. */
export const DEFAULT_SERVICES: { category: string; color: LabelColorKey; items: [string, number][] }[] = [
  {
    category: 'Design',
    color: 'cyan',
    items: [
      ['Feed', 6000],
      ['Story', 3500],
      ['Carrossel', 12000],
      ['Banner', 8000],
      ['Outdoor', 30000],
      ['Flyer', 9000],
      ['Cartaz', 15000],
      ['Embalagem', 120000],
    ],
  },
  {
    category: 'Motion',
    color: 'coral',
    items: [
      ['Reel', 18000],
      ['Story animado', 9000],
      ['Motion KV', 25000],
      ['Logo Animation', 60000],
      ['VFX', 80000],
      ['Institucional', 250000],
    ],
  },
  {
    category: 'Branding',
    color: 'lavender',
    items: [
      ['Logo', 250000],
      ['Identidade Visual', 800000],
      ['Brandbook', 300000],
      ['Naming', 150000],
      ['Key Visual', 25000],
    ],
  },
];

export const REQUEST_STATUSES = ['new', 'needs_info', 'accepted', 'rejected', 'archived'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];
export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  new: 'Nova',
  needs_info: 'Aguardando informações',
  accepted: 'Aceita',
  rejected: 'Recusada',
  archived: 'Arquivada',
};

export const ASSIGNEE_KINDS = ['responsible', 'collaborator'] as const;
export type AssigneeKind = (typeof ASSIGNEE_KINDS)[number];

export const EVENT_KINDS = ['meeting', 'review', 'publication', 'appointment', 'other'] as const;
export type EventKind = (typeof EVENT_KINDS)[number];
export const EVENT_KIND_LABELS: Record<EventKind, string> = {
  meeting: 'Reunião',
  review: 'Revisão',
  publication: 'Publicação',
  appointment: 'Compromisso',
  other: 'Evento',
};
export const RECURRENCES = ['none', 'daily', 'weekly', 'monthly'] as const;
export type Recurrence = (typeof RECURRENCES)[number];
export const RECURRENCE_LABELS: Record<Recurrence, string> = {
  none: 'Não repete',
  daily: 'Todo dia',
  weekly: 'Toda semana',
  monthly: 'Todo mês',
};

/** Pastas criadas automaticamente na biblioteca de cada cliente (SPEC §29). */
export const DEFAULT_CLIENT_FOLDERS = [
  'Logos',
  'Fotos',
  'Fontes',
  'Campanhas',
  'Vídeos',
  'Documentos',
] as const;

/** Acima disto o envio é feito em partes (retomável, com progresso real por parte). */
export const MULTIPART_THRESHOLD = 64 * 1024 ** 2;
export const MULTIPART_PART_SIZE = 16 * 1024 ** 2;

/** Creative (SPEC §14–16). drawing/shape chegam na Fase 9; o enum já nasce com eles. */
export const BOARD_KINDS = ['board', 'moodboard'] as const;
export type BoardKind = (typeof BOARD_KINDS)[number];
export const BOARD_NODE_TYPES = [
  'image',
  'video',
  'audio',
  'file',
  'text',
  'sticky',
  'card',
  'link',
  'palette',
  'typography',
  'comment',
  'group',
  'shape',
  'drawing',
] as const;
export type BoardNodeType = (typeof BOARD_NODE_TYPES)[number];
export const STICKY_COLORS = ['sand', 'amber', 'coral', 'rose', 'lavender', 'sky', 'mint', 'cyan'] as const;

/** Storyboard (SPEC §18). */
export const STORYBOARD_STATUSES = ['draft', 'review', 'approved'] as const;
export type StoryboardStatus = (typeof STORYBOARD_STATUSES)[number];
export const STORYBOARD_STATUS_LABELS: Record<StoryboardStatus, string> = {
  draft: 'Rascunho',
  review: 'Em revisão',
  approved: 'Aprovado',
};
export const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:5', '2.39:1'] as const;
export const SCENE_TRANSITIONS = [
  'Corte seco',
  'Fade',
  'Dissolve',
  'Wipe',
  'Zoom',
  'Match cut',
  'Whip pan',
  'Morph',
] as const;

/** O que pode virar link de apresentação (SPEC §19). */
export const SHARE_TARGETS = ['project', 'board', 'storyboard', 'file'] as const;
export type ShareTarget = (typeof SHARE_TARGETS)[number];

/** Aprovações (SPEC §20). */
export const APPROVAL_STATUSES = ['pending', 'approved', 'changes_requested', 'rejected'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];
export const APPROVAL_STATUS_LABELS: Record<ApprovalStatus, string> = {
  pending: 'Aguardando',
  approved: 'Aprovado',
  changes_requested: 'Alteração solicitada',
  rejected: 'Rejeitado',
};

/** Financeiro (SPEC §31–34). */
export const CONTRACT_MODELS = ['fixed', 'volume', 'hybrid', 'one_off'] as const;
export type ContractModel = (typeof CONTRACT_MODELS)[number];
export const CONTRACT_MODEL_LABELS: Record<ContractModel, string> = {
  fixed: 'Fixo mensal',
  volume: 'Por volume',
  hybrid: 'Híbrido',
  one_off: 'Avulso',
};
export const CONTRACT_STATUSES = ['draft', 'active', 'paused', 'ended'] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativo',
  paused: 'Pausado',
  ended: 'Encerrado',
};
export const RECURRENCE_PERIODS = ['monthly', 'quarterly', 'yearly', 'none'] as const;
export const INVOICE_STATUSES = [
  'draft',
  'issued',
  'pending',
  'partially_paid',
  'paid',
  'overdue',
  'cancelled',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Rascunho',
  issued: 'Emitida',
  pending: 'Pendente',
  partially_paid: 'Parcialmente paga',
  paid: 'Paga',
  overdue: 'Vencida',
  cancelled: 'Cancelada',
};
export const PAYMENT_METHODS = ['pix', 'transfer', 'boleto', 'card', 'cash', 'other'] as const;
export const PAYMENT_METHOD_LABELS: Record<(typeof PAYMENT_METHODS)[number], string> = {
  pix: 'Pix',
  transfer: 'Transferência',
  boleto: 'Boleto',
  card: 'Cartão',
  cash: 'Dinheiro',
  other: 'Outro',
};
export const DEFAULT_EXPENSE_CATEGORIES = [
  { name: 'Software', color: 'sky' },
  { name: 'Freelancer', color: 'lavender' },
  { name: 'Equipamento', color: 'amber' },
  { name: 'Transporte', color: 'mint' },
  { name: 'Assinatura', color: 'cyan' },
  { name: 'Produção', color: 'coral' },
  { name: 'Outras', color: 'sand' },
] as const;

/** R$ 1.234,56 */
export function formatMoney(cents: number, currency = 'BRL'): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency });
}

/** Templates de projeto do SPEC §38 (criados na primeira vez que a lista é aberta). */
export const DEFAULT_PROJECT_TEMPLATES = [
  {
    name: 'Identidade Visual',
    description: 'Do briefing à entrega do sistema visual.',
    tasks: [
      'Briefing',
      'Pesquisa',
      'Moodboard',
      'Conceito',
      'Logo',
      'Sistema Visual',
      'Aplicações',
      'Apresentação',
      'Entrega',
    ],
  },
  {
    name: 'Motion',
    description: 'Filme animado do roteiro ao render.',
    tasks: [
      'Briefing',
      'Referências',
      'Roteiro',
      'Storyboard',
      'Design',
      'Animação',
      'Áudio',
      'Render',
      'Revisão',
      'Entrega',
    ],
  },
  {
    name: 'Campanha',
    description: 'Conceito, KV e desdobramentos.',
    tasks: ['Briefing', 'Conceito', 'KV', 'Desdobramentos', 'Motion', 'Aprovação', 'Entrega'],
  },
] as const;
